import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

type Acquisition = {
  txnId: number;
  extTxnId: string;
  datetime: string;
  quantity: number;
  cost: number;
  costPerUnit: number;
  exchange: string;
  matchedQuantity: number;
  remainingQuantity: number;
};

type MatchedBuy = {
  txnId: number;
  extTxnId: string;
  datetime: string;
  quantity: number;
  cost: number;
  exchange: string;
  holdingPeriodDays: number | null;
};

type Disposal = {
  txnId: number;
  extTxnId: string;
  datetime: string;
  quantity: number;
  proceeds: number;
  cost: number;
  gain: number;
  fees: number;
  capitalTerm: string;
  exchange: string;
  disposalType: string;
  matchedBuy: MatchedBuy | null;
};

type TaxSummary = {
  totalProceeds: number;
  totalCostBasis: number;
  netGainLoss: number;
  totalFees: number;
  disposalCount: number;
};

type PortfolioSummary = {
  totalAcquired: number;
  totalDisposed: number;
  remainingHoldings: number;
  averageCostBasis: number;
};

export type TaxAssetBreakdown = {
  asset: string;
  taxYear: number | "all";
  taxSummary: TaxSummary;
  portfolioSummary: PortfolioSummary;
  acquisitions: Acquisition[];
  disposalsByYear: Record<string, Disposal[]>;
};

export const useTaxAssetBreakdown = ({
  symbol,
  taxYear,
  enabled = true,
}: {
  symbol: string;
  taxYear?: number;
  enabled?: boolean;
}): {
  isLoading: boolean;
  isIdle: boolean;
  error: Error | null;
  data: TaxAssetBreakdown | undefined;
  refetch: () => void;
} => {
  const { getToken, user } = useAuth();

  const getAssetBreakdown = async (): Promise<TaxAssetBreakdown> => {
    const params = new URLSearchParams();
    if (taxYear) {
      params.set("year", taxYear.toString());
    }
    const url = `/tax/asset/${encodeURIComponent(symbol)}/breakdown${
      params.toString() ? `?${params.toString()}` : ""
    }`;

    try {
      const token = await getToken();
      const res = await authorizedAxios(token).get(url);

      console.log("[useTaxAssetBreakdown] response", res?.data);
      return res.data;
    } catch (error) {
      console.error("[useTaxAssetBreakdown] error", error);
      throw error;
    }
  };

  const {
    isLoading,
    isIdle,
    error,
    data,
    refetch,
  }: UseQueryResult<TaxAssetBreakdown, Error> = useQuery({
    queryKey: ["tax_asset_breakdown", symbol, taxYear],
    queryFn: getAssetBreakdown,
    enabled: !!user?.email && !!symbol && enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    keepPreviousData: true,
  });

  return { isLoading, isIdle, error, data, refetch };
};
